/**
 * Step — Frames. Popularity-ranked public templates with server-side title
 * search and "load more" pagination (GET /api/onboarding/frames), plus the
 * AI frame card that opens the simplified generator modal. The creator's own
 * frames (returned as `mine` on page 1, merged with any generated during
 * this flow) lead the grid with a "Yours" badge.
 *
 * EVERY BOOTH LEAVES HERE WITH FRAMES. Nothing was selected by default, and
 * the create route switches the frame page off entirely when the selection is
 * empty (`enabled: frameIds.length > 0`) — so skipping this step shipped a
 * booth with no strips at all. On first load we now pre-select three, matched
 * against the booth's own frameTags/filterMood and falling back to the
 * popularity order the endpoint already returns. Signed in or not.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FaSpinner, FaImages, FaSearch, FaPenNib } from 'react-icons/fa'
import { useOnboardingStore } from '../onboardingStore'
import SelectableCard from '../components/SelectableCard'
import AiFrameModal from '../components/AiFrameModal'
import {
  exploreFrames,
  fetchDraftFrames,
  type CatalogFrame,
  type BoothSpecDto
} from '../onboardingApi'
import { useCopy } from '../regionCopy'

const PAGE_SIZE = 24
const STARTER_FRAME_COUNT = 3
/** Polling for frames another request is already drawing. Bounded on purpose. */
const DRAW_POLL_ATTEMPTS = 6
const DRAW_POLL_INTERVAL_MS = 8000

/**
 * Picks the starter frames for a freshly generated booth. `pool` arrives in
 * popularity order, so an unmatched booth still gets the three templates most
 * guests actually use; a booth whose spec named "scrapbook"/"y2k" gets those
 * first. Ties keep the incoming order.
 */
function pickStarterFrames(pool: CatalogFrame[], spec: BoothSpecDto | null): string[] {
  const terms = [...(spec?.frameTags ?? []), spec?.filterMood ?? '']
    .flatMap((t) => String(t).toLowerCase().split(/[^a-z0-9]+/))
    .filter((t) => t.length >= 3)

  return pool
    .map((frame, order) => {
      const name = frame.name.toLowerCase()
      const hits = terms.reduce((n, term) => (name.includes(term) ? n + 1 : n), 0)
      return { frame, hits, order }
    })
    .sort((a, b) => b.hits - a.hits || a.order - b.order)
    .slice(0, STARTER_FRAME_COUNT)
    .map((entry) => entry.frame._id)
}

interface StepFramesV2Props {
  /** Frames generated + saved by this creator during the flow. */
  myFrames: CatalogFrame[]
  onSavedFrame: (frame: CatalogFrame) => void
  signedIn: boolean
  onRequestSignIn: () => Promise<boolean>
}

export const StepFramesV2: React.FC<StepFramesV2Props> = ({
  myFrames,
  onSavedFrame,
  signedIn,
  onRequestSignIn
}) => {
  const frameIds = useOnboardingStore((s) => s.frameIds)
  const toggleFrame = useOnboardingStore((s) => s.toggleFrame)
  const setFrameIds = useOnboardingStore((s) => s.setFrameIds)
  const spec = useOnboardingStore((s) => s.spec)
  const draftId = useOnboardingStore((s) => s.draftId)
  const draftFrames = useOnboardingStore((s) => s.draftFrames)
  const setDraftFrames = useOnboardingStore((s) => s.setDraftFrames)
  const boothName = useOnboardingStore((s) => s.name)
  const c = useCopy()

  const [query, setQuery] = useState('')
  const [items, setItems] = useState<CatalogFrame[]>([])
  const [mine, setMine] = useState<CatalogFrame[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [authBusy, setAuthBusy] = useState(false)
  const [showStarterNote, setShowStarterNote] = useState(false)
  const [drawingFrames, setDrawingFrames] = useState(false)

  const autoPickLatched = useRef(false)
  const drawLatched = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeq = useRef(0)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const load = async (search: string, nextPage: number, append: boolean): Promise<void> => {
    const seq = ++requestSeq.current
    if (append) setLoadingMore(true)
    else setLoading(true)
    try {
      const res = await exploreFrames({
        search: search || undefined,
        page: nextPage,
        pageSize: PAGE_SIZE,
        mode: useOnboardingStore.getState().primaryMode()
      })
      if (seq !== requestSeq.current) return // stale response
      setItems((prev) => (append ? [...prev, ...res.items] : res.items))
      if (nextPage === 1) setMine(res.mine)
      setPage(res.page)
      setHasMore(res.hasMore)
    } catch (err) {
      console.error('[onboarding] frames explore failed:', err)
      if (!append) setItems([])
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }

  // Initial load + debounced server search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void load(query.trim(), 1, false)
    }, 350)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // Infinite scroll: fetch the next page when the sentinel nears the viewport.
  // Recreated on state change so the callback never sees stale page/hasMore.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasMore || loading || loadingMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void load(query.trim(), page + 1, true)
        }
      },
      { rootMargin: '300px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loading, loadingMore, page, query])

  /**
   * The booth's OWN frames, drawn from its spec. Requested here rather than
   * during generation: the image quota cannot absorb three more calls in that
   * request, and by the time a creator walks onto this step the earlier steps
   * have bought exactly the headroom it needs. Server-side idempotent, so a
   * re-mount or a second tab never redraws.
   */
  useEffect(() => {
    if (drawLatched.current || !draftId) return
    if (draftFrames.length > 0) {
      drawLatched.current = true
      return
    }
    drawLatched.current = true

    let cancelled = false
    setDrawingFrames(true)

    /**
     * Ask for the booth's own frames, and give up quietly if they do not
     * come. This must never gate the step: the catalogue below is a complete
     * answer on its own, and a creator who cannot see frames because a bonus
     * feature failed would reasonably think the product is broken.
     *
     * "drawing" means another request (another tab, an earlier mount) already
     * has the job. Poll a few times, then stop — they will be there on the
     * next visit, and the auto-pick below has already given them a strip.
     */
    const run = async (attempt = 0): Promise<void> => {
      const res = await fetchDraftFrames(draftId)
      if (cancelled) return
      if (res.frames.length > 0) {
        setDraftFrames(res.frames)
        setDrawingFrames(false)
        return
      }
      if (res.status === 'drawing' && attempt < DRAW_POLL_ATTEMPTS) {
        window.setTimeout(() => {
          if (!cancelled) void run(attempt + 1)
        }, DRAW_POLL_INTERVAL_MS)
        return
      }
      setDrawingFrames(false)
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [draftId, draftFrames.length, setDraftFrames])

  // Drawn frames read as the creator's own, and lead the grid.
  const drawnAsCatalog = useMemo<CatalogFrame[]>(
    () =>
      draftFrames.map((f) => ({
        _id: `drawn:${f.templateId}`,
        name: f.name,
        owner: '',
        thumbnail: f.url,
        source: 'ai',
        drawParams: { canvasWidth: 0, canvasHeight: 0 }
      })),
    [draftFrames]
  )

  const ownFrames = useMemo(() => {
    const seen = new Set<string>()
    const merged: Array<CatalogFrame & { mine: true }> = []
    for (const f of [...drawnAsCatalog, ...myFrames, ...mine]) {
      if (seen.has(f._id)) continue
      seen.add(f._id)
      merged.push({ ...f, mine: true })
    }
    const q = query.trim().toLowerCase()
    return q ? merged.filter((f) => f.name.toLowerCase().includes(q)) : merged
  }, [drawnAsCatalog, myFrames, mine, query])

  const publicFrames = useMemo(
    () => items.filter((f) => !ownFrames.some((m) => m._id === f._id)),
    [items, ownFrames]
  )

  // Pre-select three, once, from the unfiltered first page. Latched so that a
  // creator who deliberately clears the selection is never overruled.
  useEffect(() => {
    // Wait for the booth's own frames before choosing — they should be the
    // default, not whatever the catalogue happened to rank first.
    // Deliberately NOT gated on `drawingFrames`: if the booth's own frames
    // are slow or never arrive, the creator still leaves this step with a
    // strip selected. They lead the grid and get picked first when they land.
    if (autoPickLatched.current || loading || query.trim()) return
    if (frameIds.length > 0) {
      autoPickLatched.current = true
      return
    }
    const pool = [...ownFrames, ...publicFrames]
    if (pool.length === 0) return
    autoPickLatched.current = true
    const picked = pickStarterFrames(pool, spec)
    if (picked.length === 0) return
    setFrameIds(picked)
    setShowStarterNote(true)
  }, [loading, query, frameIds.length, ownFrames, publicFrames, spec, setFrameIds])

  // When the booth's own frames arrive after the catalogue pick, put them in
  // front — they are the ones designed for this booth.
  useEffect(() => {
    if (draftFrames.length === 0 || !autoPickLatched.current) return
    const drawnIds = drawnAsCatalog.map((f) => f._id)
    const alreadyLeading = drawnIds.every((id) => frameIds.includes(id))
    if (alreadyLeading) return
    setFrameIds([...drawnIds, ...frameIds.filter((id) => !drawnIds.includes(id))].slice(0, 6))
  }, [draftFrames.length, drawnAsCatalog, frameIds, setFrameIds])

  const openModal = async (): Promise<void> => {
    if (signedIn) {
      setModalOpen(true)
      return
    }
    setAuthBusy(true)
    const ok = await onRequestSignIn()
    setAuthBusy(false)
    if (ok) setModalOpen(true)
  }

  const empty = ownFrames.length === 0 && publicFrames.length === 0

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-4 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{c.frames}</h2>
          <p className="text-sm text-whiteout/50">
            {c.framesSub}{' '}
            {frameIds.length > 0 && (
              <span className="text-blueSparkle">{c.nSelected(frameIds.length)}</span>
            )}
          </p>
        </div>
        <label className="relative w-full sm:w-auto">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-whiteout/30" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={c.searchFrames}
            className="w-full rounded-full border border-white/15 bg-white/[0.04] py-2 pl-8 pr-3 text-xs text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle sm:w-52"
          />
        </label>
      </header>

      {drawingFrames && (
        <p className="flex items-center gap-2 rounded-xl border border-blueSparkle/25 bg-blueSparkle/10 px-4 py-2.5 text-xs text-whiteout/70">
          <FaSpinner className="animate-spin" /> {c.drawingFrames}
        </p>
      )}

      {showStarterNote && frameIds.length > 0 && (
        <p className="rounded-xl border border-blueSparkle/25 bg-blueSparkle/10 px-4 py-2.5 text-xs text-whiteout/70">
          {c.starterFramesNote(frameIds.length)}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto soft-scrollbar pb-4 pr-1">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-whiteout/50">
            <FaSpinner className="animate-spin text-2xl" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {/* AI generate card — always first */}
              <button
                type="button"
                onClick={openModal}
                disabled={authBusy}
                className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-blueSparkle/50 bg-blueSparkle/5 text-blueSparkle transition-colors hover:bg-blueSparkle/15 disabled:opacity-50"
              >
                {authBusy ? (
                  <FaSpinner className="animate-spin text-2xl" />
                ) : (
                  <FaPenNib className="text-2xl" />
                )}
                <span className="px-3 text-center text-xs font-medium">{c.designYourOwn}</span>
                {!signedIn && (
                  <span className="px-3 text-center text-[10px] text-whiteout/40">
                    {c.signInRequired}
                  </span>
                )}
              </button>

              {[...ownFrames, ...publicFrames].map((frame) => (
                <SelectableCard
                  key={frame._id}
                  selected={frameIds.includes(frame._id)}
                  onClick={() => toggleFrame(frame._id)}
                  ariaLabel={frame.name}
                  className="bg-white/[0.04] p-0"
                >
                  <div className="relative flex aspect-[3/4] w-full items-center justify-center bg-black/30 p-2">
                    <img
                      src={frame.thumbnail}
                      alt={frame.name}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain"
                    />
                    {(frame as { mine?: boolean }).mine && (
                      <span className="absolute left-1.5 top-1.5 rounded-full bg-blueSparkle/90 px-2 py-0.5 text-[9px] font-semibold text-whiteout">
                        {c.yours}
                      </span>
                    )}
                  </div>
                  <div className="truncate px-2 py-1.5 text-[11px] text-whiteout/70">
                    {frame.name}
                  </div>
                </SelectableCard>
              ))}

              {empty && (
                <div className="col-span-full flex flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] py-12 text-center text-whiteout/50">
                  <FaImages className="text-3xl" />
                  <p>
                    {query.trim() ? c.noFramesMatch(query) : c.noFrames}
                  </p>
                </div>
              )}
            </div>

            {hasMore && (
              // Infinite scroll: the sentinel triggers the next page as it
              // approaches the viewport — no button click needed.
              <div ref={sentinelRef} className="flex justify-center pt-4">
                {loadingMore && (
                  <span className="inline-flex items-center gap-2 py-2 text-xs text-whiteout/50">
                    <FaSpinner className="animate-spin" /> {c.loadingMoreFrames}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {modalOpen && (
        <AiFrameModal
          boothName={boothName}
          onClose={() => setModalOpen(false)}
          onSaved={(frame) => {
            onSavedFrame(frame)
            if (!frameIds.includes(frame._id)) toggleFrame(frame._id)
          }}
        />
      )}
    </div>
  )
}

export default StepFramesV2
